import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider, GoogleLogin as GoogleLoginButton } from '@react-oauth/google';
import { login, googleLogin } from '../../services/auth';
import config from '../../config';

const Login = ({ onLogin, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setMounted(true);
    document.body.classList.add('public-page');
    return () => {
      document.body.classList.remove('public-page');
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData);

    if (result.success) {
      // Use replace to navigate and load fresh - this changes URL and loads the page
      window.location.replace('/prompts');
    } else {
      setError(result.error || 'Invalid username or password');
    }

    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    const result = await googleLogin(credentialResponse.credential);

    if (result.success) {
      // Use replace to navigate and load fresh - this changes URL and loads the page
      window.location.replace('/prompts');
    } else {
      setError(result.error || 'Google sign-in failed');
    }

    setLoading(false);
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed');
  };

  const features = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      ),
      text: 'Create AI features in plain English'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </svg>
      ),
      text: 'Compare ChatGPT, Claude & Llama side-by-side'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
        </svg>
      ),
      text: 'Never lose your work with version history'
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      text: 'Deploy via SDK when you\'re ready'
    },
  ];

  return (
    <div className="min-h-screen w-full flex bg-bg-primary">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Aurora gradient layers */}
        <div className="absolute inset-0 aurora-bg-login" />
        
        {/* Large floating orbs */}
        <div className="absolute rounded-full orb-login-1" />
        <div className="absolute rounded-full orb-login-2" />
        <div className="absolute rounded-full orb-login-3" />


        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className={`absolute rounded-full particle particle-${(i % 3) + 1}`}
            style={{
              width: `${2 + (i % 3) * 2}px`,
              height: `${2 + (i % 3) * 2}px`,
              left: `${8 + (i * 6) % 85}%`,
              top: `${15 + (i * 5) % 70}%`,
              animationDelay: `${i * 0.25}s`,
            }}
          />
        ))}

        {/* Glowing accent lines */}
        <svg className="absolute inset-0 w-full h-full opacity-30">
          <defs>
            <linearGradient id="loginLineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(50, 205, 50, 0)" />
              <stop offset="50%" stopColor="rgba(50, 205, 50, 0.4)" />
              <stop offset="100%" stopColor="rgba(50, 205, 50, 0)" />
            </linearGradient>
          </defs>
          <line x1="0%" y1="40%" x2="60%" y2="100%" stroke="url(#loginLineGrad)" strokeWidth="1" className="line-pulse" />
          <line x1="40%" y1="0%" x2="100%" y2="60%" stroke="url(#loginLineGrad)" strokeWidth="0.5" className="line-pulse-reverse" />
        </svg>
      </div>

      {/* Navigation Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-4 transition-all duration-600 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-5'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img 
              src="/logo.png"
              alt="ForgeVault Logo" 
              className="w-9 h-9 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="text-lg font-bold transition-colors duration-300 group-hover:text-primary-400 text-text-primary">ForgeVault</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Home
            </Link>
            <Link to="/features" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Features
            </Link>
            <Link to="/docs" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Docs
            </Link>
            <Link to="/about" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              About
            </Link>
            <Link to="/contact" className="text-sm font-medium transition-colors duration-300 text-primary-300/70 hover:text-primary-500">
              Contact
            </Link>
          </nav>
          
          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <span className="px-4 py-2 text-sm font-medium rounded-lg text-primary-500 bg-primary-500/10">
              Sign In
            </span>
            <Link
              to="/signup"
              className="px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-primary-600 to-primary-500 text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Left Panel - Branding */}
      <div 
        className={`hidden lg:flex lg:w-1/2 relative items-center justify-center px-12 transition-all duration-1000 ease-out ${
          mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
        }`}
      >
        {/* Decorative floating shapes */}
        <div className="absolute w-32 h-32 rounded-3xl rotate-12 decorative-shape-login-1" />
        <div className="absolute w-24 h-24 rounded-2xl -rotate-12 decorative-shape-login-2" />
        <div className="absolute w-16 h-16 rounded-xl rotate-45 decorative-shape-login-3" />

        <div className="relative z-10 max-w-md">
          {/* Logo */}
          <img 
            src="/logo.png"
            alt="ForgeVault Logo" 
            className={`w-20 h-20 object-contain mb-8 transition-all duration-800 ease-bounce ${
              mounted ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 -rotate-[10deg]'
            }`}
            style={{ transitionDelay: '0.2s' }}
          />

          {/* Title */}
          <h1 
            className={`text-4xl font-bold mb-4 tracking-tight text-text-primary transition-all duration-800 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '0.3s' }}
          >
            Welcome back
          </h1>
          
          <p 
            className={`text-lg mb-10 leading-relaxed text-primary-200/70 transition-all duration-800 ease-out ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            Your prompts, your models, your workflow. Continue building AI features that ship faster.
          </p>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-4 group transition-all duration-600 ease-out ${
                  mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: `${0.5 + idx * 0.1}s` }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 bg-primary-500/10 border border-primary-500/15 text-primary-500 shadow-glow-sm">
                  {feature.icon}
                </div>
                <span className="transition-colors duration-300 text-text-primary/80 group-hover:text-primary-300">
                  {feature.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 relative z-10">
        <div 
          className={`w-full max-w-sm transition-all duration-800 ease-out ${
            mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
          }`}
          style={{ transitionDelay: '0.2s' }}
        >
          <div className="rounded-2xl p-8 relative overflow-hidden bg-bg-secondary/90 backdrop-blur-lg border border-primary-500/15 shadow-2xl">
            {/* Glow effect */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-primary-500/8 to-transparent" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />

            {/* Mobile logo */}
            <div className="lg:hidden text-center mb-6">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-14 h-14 mx-auto mb-3 object-contain"
              />
              <h2 className="text-lg font-bold text-text-primary">ForgeVault</h2>
            </div>

            <div className="relative z-10">
              <h2 className="text-xl font-semibold mb-1 text-text-primary">
                Welcome back
              </h2>
              <p className="text-sm mb-6 text-primary-200/50">
                Sign in to your account
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label 
                    htmlFor="username" 
                    className="block text-sm font-medium mb-1.5 text-text-secondary/90"
                  >
                    Username
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-gradient-to-br from-primary-500/15 to-primary-600/10 blur-lg" />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-300 focus:outline-none relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                      placeholder="Enter your username"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <label 
                    htmlFor="password" 
                    className="block text-sm font-medium mb-1.5 text-text-secondary/90"
                  >
                    Password
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none bg-gradient-to-br from-primary-500/15 to-primary-600/10 blur-lg" />
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-300 focus:outline-none relative z-10 bg-bg-input/80 border border-primary-500/15 text-text-primary focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/10 placeholder:text-primary-200/30"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                {/* Inline Error Message */}
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-error/10 border border-error/30 text-red-400">
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="w-full py-3.5 text-sm font-semibold rounded-xl cursor-pointer transition-all duration-300 inline-flex items-center justify-center gap-2 border-none disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg"
                  disabled={loading}
                >
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shimmer" />
                  <span className="relative z-10 flex items-center gap-2">
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                        </svg>
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign In
                        <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </>
                    )}
                  </span>
                </button>
              </form>

              {/* Google Sign-In */}
              {config.google.clientId && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-primary-500/15" />
                    <span className="text-xs text-primary-200/50">or continue with</span>
                    <div className="flex-1 h-px bg-primary-500/15" />
                  </div>

                  <div className="flex justify-center">
                    <GoogleOAuthProvider clientId={config.google.clientId}>
                      <GoogleLoginButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        theme="filled_black"
                        size="large"
                        width="100%"
                        text="signin_with"
                        shape="rectangular"
                      />
                    </GoogleOAuthProvider>
                  </div>
                </>
              )}

              <div className="mt-6 pt-5 text-center border-t border-primary-500/10">
                <p className="text-sm text-primary-200/50">
                  Don't have an account?{' '}
                  <button 
                    type="button" 
                    onClick={onSwitchToRegister} 
                    className="font-semibold cursor-pointer p-0 transition-all duration-300 hover:underline bg-transparent border-none text-primary-500 hover:drop-shadow-[0_0_10px_rgba(50,205,50,0.5)]"
                  >
                    Sign up
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .aurora-bg-login {
          background: 
            radial-gradient(ellipse 100% 70% at 30% 20%, rgba(50, 205, 50, 0.18) 0%, transparent 50%),
            radial-gradient(ellipse 80% 60% at 70% 70%, rgba(34, 139, 34, 0.12) 0%, transparent 45%),
            radial-gradient(ellipse 60% 50% at 90% 30%, rgba(50, 205, 50, 0.08) 0%, transparent 40%);
          animation: auroraShift 12s ease-in-out infinite alternate;
        }
        .orb-login-1 {
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.2) 0%, rgba(50, 205, 50, 0.05) 40%, transparent 70%);
          top: -15%;
          left: -10%;
          filter: blur(60px);
          animation: floatOrb1 28s ease-in-out infinite;
        }
        .orb-login-2 {
          width: 550px;
          height: 550px;
          background: radial-gradient(circle, rgba(34, 139, 34, 0.15) 0%, rgba(34, 139, 34, 0.03) 45%, transparent 70%);
          bottom: -5%;
          right: -10%;
          filter: blur(50px);
          animation: floatOrb2 32s ease-in-out infinite;
        }
        .orb-login-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(50, 205, 50, 0.12) 0%, transparent 60%);
          top: 50%;
          left: 30%;
          filter: blur(45px);
          animation: floatOrb3 22s ease-in-out infinite;
        }
        .particle {
          background: rgba(50, 205, 50, 0.7);
          box-shadow: 0 0 8px rgba(50, 205, 50, 0.4);
        }
        .particle:nth-child(even) {
          background: rgba(152, 251, 152, 0.5);
        }
        .particle-1 { animation: floatParticle1 10s ease-in-out infinite; }
        .particle-2 { animation: floatParticle2 12s ease-in-out infinite; }
        .particle-3 { animation: floatParticle3 8s ease-in-out infinite; }
        .line-pulse { animation: linePulse 6s ease-in-out infinite; }
        .line-pulse-reverse { animation: linePulse 8s ease-in-out infinite reverse; }
        .decorative-shape-login-1 {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.2) 0%, rgba(34, 139, 34, 0.05) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(50, 205, 50, 0.15);
          top: 15%;
          left: 10%;
          animation: floatShape1 15s ease-in-out infinite;
        }
        .decorative-shape-login-2 {
          background: linear-gradient(135deg, rgba(34, 139, 34, 0.15) 0%, rgba(50, 205, 50, 0.05) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(34, 139, 34, 0.1);
          bottom: 20%;
          right: 15%;
          animation: floatShape2 18s ease-in-out infinite;
        }
        .decorative-shape-login-3 {
          background: linear-gradient(135deg, rgba(50, 205, 50, 0.12) 0%, transparent 100%);
          border: 1px solid rgba(50, 205, 50, 0.1);
          top: 50%;
          right: 25%;
          animation: floatShape3 12s ease-in-out infinite;
        }
        .shimmer {
          background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%);
          animation: shimmer 2s infinite;
        }
        @keyframes auroraShift {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.03); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(60px, 30px) scale(1.1); }
          50% { transform: translate(30px, 60px) scale(0.95); }
          75% { transform: translate(-30px, 30px) scale(1.05); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-50px, -40px) scale(1.12); }
          50% { transform: translate(-80px, 15px) scale(0.92); }
          75% { transform: translate(-25px, -25px) scale(1.05); }
        }
        @keyframes floatOrb3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, -35px) scale(1.08); }
          66% { transform: translate(25px, 40px) scale(0.96); }
        }
        @keyframes floatParticle1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          25% { transform: translate(12px, -20px) scale(1.2); opacity: 0.9; }
          50% { transform: translate(25px, -8px) scale(0.9); opacity: 0.6; }
          75% { transform: translate(8px, 12px) scale(1.1); opacity: 0.8; }
        }
        @keyframes floatParticle2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          33% { transform: translate(-18px, -25px) scale(1.25); opacity: 1; }
          66% { transform: translate(20px, -12px) scale(0.85); opacity: 0.5; }
        }
        @keyframes floatParticle3 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(-12px, 20px) scale(1.15); opacity: 1; }
        }
        @keyframes linePulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.5; }
        }
        @keyframes floatShape1 {
          0%, 100% { transform: translate(0, 0) rotate(12deg); }
          50% { transform: translate(15px, -20px) rotate(18deg); }
        }
        @keyframes floatShape2 {
          0%, 100% { transform: translate(0, 0) rotate(-12deg); }
          50% { transform: translate(-20px, 15px) rotate(-6deg); }
        }
        @keyframes floatShape3 {
          0%, 100% { transform: translate(0, 0) rotate(45deg); }
          50% { transform: translate(10px, 15px) rotate(50deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

Login.propTypes = {
  onLogin: PropTypes.func.isRequired,
  onSwitchToRegister: PropTypes.func.isRequired,
};

export default Login;
